import { useRef } from "react";
import type { RunProgress } from "../types/shadow-run";
import { formatNumber, formatPercentage } from "../utils/formatters";

export interface ActivityLogEntry {
  id: string;
  timestamp: Date;
  message: string;
  level: "info" | "success" | "warning" | "error";
}

const MAX_ENTRIES = 50;

let nextId = 0;
function createEntry(
  message: string,
  level: ActivityLogEntry["level"],
): ActivityLogEntry {
  return {
    id: `activity-${++nextId}`,
    timestamp: new Date(),
    message,
    level,
  };
}

export function useActivityLog(progress: RunProgress): ActivityLogEntry[] {
  const prevRef = useRef<RunProgress | null>(null);
  const entriesRef = useRef<ActivityLogEntry[]>([]);
  const initializedRef = useRef(false);

  const prev = prevRef.current;

  if (!initializedRef.current) {
    initializedRef.current = true;
    entriesRef.current = [createEntry("Monitoring shadow run\u2026", "info")];
  } else if (prev) {
    const newEntries: ActivityLogEntry[] = [];

    // Status transition
    if (prev.status !== progress.status) {
      if (progress.status === "running") {
        newEntries.push(createEntry("Run started", "info"));
      } else if (progress.status === "completed") {
        newEntries.push(
          createEntry(
            `Run completed \u2014 ${formatPercentage(progress.match_rate)} match rate`,
            "success",
          ),
        );
      } else if (progress.status === "failed") {
        newEntries.push(createEntry("Run failed", "error"));
      }
    }

    // Total packets discovered
    if (prev.total_packets === 0 && progress.total_packets > 0) {
      newEntries.push(
        createEntry(
          `Found ${formatNumber(progress.total_packets)} packets to process`,
          "info",
        ),
      );
    }

    // Processed packets delta
    if (progress.processed_packets > prev.processed_packets) {
      newEntries.push(
        createEntry(
          `Processed ${formatNumber(progress.processed_packets)}/${formatNumber(progress.total_packets)} packets`,
          "info",
        ),
      );
    }

    // Message changed
    if (
      progress.message &&
      progress.message !== prev.message
    ) {
      newEntries.push(createEntry(progress.message, "info"));
    }

    // Circuit breaker state changed
    if (
      prev.circuit_breaker_state !== progress.circuit_breaker_state
    ) {
      const level: ActivityLogEntry["level"] =
        progress.circuit_breaker_state === "TRIPPED" ? "error" : "warning";
      newEntries.push(
        createEntry(
          `Circuit breaker: ${progress.circuit_breaker_state}`,
          level,
        ),
      );
    }

    // Error rate increased by more than 0.5 percentage points
    if (progress.error_rate - prev.error_rate > 0.5) {
      newEntries.push(
        createEntry(
          `Error rate: ${formatPercentage(progress.error_rate)}`,
          "warning",
        ),
      );
    }

    if (newEntries.length > 0) {
      entriesRef.current = [
        ...entriesRef.current,
        ...newEntries,
      ].slice(-MAX_ENTRIES);
    }
  }

  prevRef.current = progress;

  return entriesRef.current;
}
